import React, { useState } from "react";
import {
  CloudDoneRounded, DevicesRounded, SearchRounded, ShieldRounded,
  SmartphoneRounded, RefreshRounded, StorageRounded, CheckCircleRounded,
  WarningAmberRounded
} from "@mui/icons-material";
import { fdcRequest } from "../services/fdcApi";
import { useSearchParams } from "react-router-dom";
import "./UniCloudManagement.css";

export default function UniCloudManagement({staffId}) {
  const [params]=useSearchParams();
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [cloudData, setCloudData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingCloud, setLoadingCloud] = useState(false);
  const [error, setError] = useState("");

  const normalizedId = clientId.trim();

  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

  const checkStatus = async () => {
    if (!normalizedId) return setError("Enter a client ID first.");
    setError("");
    setLoadingStatus(true);
    try {
      const result = await fdcRequest(staffId,`clients/${encodeURIComponent(normalizedId)}/device`);
      setDeviceStatus(result);
    } catch (err) {
      setDeviceStatus(null);
      setError(getErrorMessage(err, "Failed to check UniCloud device status."));
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchCloudData = async () => {
    if (!normalizedId) return setError("Enter a client ID first.");
    setError("");
    setLoadingCloud(true);
    try {
      const result = await fdcRequest(staffId,`clients/${encodeURIComponent(normalizedId)}/cloud`);
      setCloudData(result);
    } catch (err) {
      setCloudData(null);
      setError(getErrorMessage(err, "Failed to fetch UniCloud data."));
    } finally {
      setLoadingCloud(false);
    }
  };

  const records = cloudData?.uniCloudResult || [];

  return (
    <div className="ucPage">
      <section className="ucHero">
        <div>
          <span className="ucEyebrow"><CloudDoneRounded/> UniCloud operations</span>
          <h2>Client UniCloud management</h2>
          <p>Check the registered mobile device and inspect the client's UniCloud records directly from your existing backend.</p>
        </div>
        <div className="ucBackendBadge"><ShieldRounded/><span><strong>Your client portfolio</strong><small>Authorized FDC access</small></span></div>
      </section>

      <section className="ucLookupPanel">
        <div className="ucLookupHeading">
          <div><span className="ucSectionKicker">Client lookup</span><h3>Find a UniCloud client</h3><p>Use the same <code>clientId</code> stored in <code>universe_erp_clients</code>.</p></div>
        </div>
        <div className="ucLookup">
          <label><SearchRounded/><input value={clientId} onChange={e=>setClientId(e.target.value)}
            onKeyDown={e=>e.key==="Enter" && checkStatus()} placeholder="Enter client ID..." /></label>
          <button className="ucPrimary" disabled={loadingStatus} onClick={checkStatus}>
            {loadingStatus ? <RefreshRounded className="ucSpin"/> : <DevicesRounded/>}
            {loadingStatus ? "Checking..." : "Check device"}
          </button>
          <button className="ucSecondary" disabled={loadingCloud} onClick={fetchCloudData}>
            {loadingCloud ? <RefreshRounded className="ucSpin"/> : <StorageRounded/>}
            {loadingCloud ? "Loading..." : "Fetch cloud data"}
          </button>
        </div>
        {error && <div className="ucError"><WarningAmberRounded/><span>{error}</span></div>}
      </section>

      <section className="ucLiveGrid">
        <article className="ucPanel ucDevicePanel">
          <div className="ucPanelTitle"><div><span className="ucSectionKicker">Device status</span><h3>Registered device</h3></div><SmartphoneRounded/></div>
          {!deviceStatus ? (
            <div className="ucAwaiting"><DevicesRounded/><h4>No client checked yet</h4><p>Enter a client ID above to check device registration.</p></div>
          ) : deviceStatus.registered ? (
            <div className="ucRegistered">
              <div className="ucRegisteredHero"><div className="ucPhoneIcon"><SmartphoneRounded/></div><div><span className="ucStatus active"><i/>Activated</span><h4>{deviceStatus.device?.platform || "Registered device"}</h4><p>Platform version {deviceStatus.device?.platformVersion || "Not supplied"}</p></div></div>
              <div className="ucDetails">
                <div><span>Client ID</span><strong>{normalizedId}</strong></div>
                <div><span>Registration</span><strong>UniCloud active</strong></div>
                <div><span>Platform</span><strong>{deviceStatus.device?.platform || "—"}</strong></div>
                <div><span>Platform version</span><strong>{deviceStatus.device?.platformVersion || "—"}</strong></div>
              </div>
            </div>
          ) : (
            <div className="ucAwaiting pending"><WarningAmberRounded/><h4>No UniCloud device registered</h4><p>This client exists, but the backend did not find an FCM token for the client's UniCloud device.</p></div>
          )}
        </article>

        <article className="ucPanel">
          <div className="ucPanelTitle"><div><span className="ucSectionKicker">Cloud records</span><h3>UniCloud data</h3></div><StorageRounded/></div>
          {!cloudData ? (
            <div className="ucAwaiting"><StorageRounded/><h4>Cloud data not loaded</h4><p>Fetch the client's UniCloud collection when you need to inspect it.</p></div>
          ) : (
            <div className="ucCloudSummary">
              <div className="ucCloudCount"><strong>{records.length}</strong><span>record{records.length === 1 ? "" : "s"} returned</span></div>
              <div className="ucCloudRecords">
                {records.slice(0,5).map((record,index)=><div key={record?._id || index}>
                  <CheckCircleRounded/><div><strong>{record?.type || record?.heading || `UniCloud record ${index+1}`}</strong><span>{record?.message || record?.description || record?.createdAt || "Cloud record available"}</span></div>
                </div>)}
              </div>
              {records.length > 5 && <p className="ucMoreRecords">+ {records.length-5} additional records returned by the API.</p>}
            </div>
          )}
        </article>
      </section>

      <section className="ucInfoCard">
        <div className="ucInfoIcon"><ShieldRounded/></div>
        <div>
          <span className="ucSectionKicker">Important activation architecture</span>
          <h3>Device registration requires the real FCM token</h3>
          <p>Your <code>register-device</code> endpoint requires both <code>clientId</code> and <code>fcmToken</code>. The FDC web dashboard should not manufacture that token. The UniCloud mobile application should obtain its real FCM token and register the device through your existing endpoint. This dashboard can then verify the activation using the status endpoint.</p>
        </div>
      </section>
    </div>
  );
}
